import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { MODULES } from "../content";
import { usePromotion } from "../hooks/usePromotion";
import Countdown from "./Countdown";
import BuyButton from "./BuyButton";
import Reveal from "./Reveal";

function ModuleItem({ mod, open, onToggle }) {
  const panelId = `module-panel-${mod.number}`;
  return (
    <div
      data-testid={`program-module-accordion-${mod.number}`}
      className="overflow-hidden rounded-2xl border border-[#EAE6F0] bg-white transition-colors duration-200 hover:border-[#FFB3DA]"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-start gap-4 px-5 py-5 text-left sm:px-7"
      >
        <span className="mt-0.5 shrink-0 rounded-lg bg-[#FDF4F8] px-2.5 py-1 text-xs font-extrabold uppercase tracking-wide text-[#FF008B]">
          Moduł {mod.number}
        </span>
        <span className="flex-1">
          <span className="block text-base font-bold leading-snug text-[#242344] sm:text-lg">
            {mod.isNew && (
              <span className="mr-2 rounded-md bg-[#FF008B] px-2 py-0.5 align-middle text-[0.65rem] font-extrabold uppercase tracking-wider text-white">
                Nowość!
              </span>
            )}
            {mod.title}
          </span>
          <span className="mt-1.5 block text-sm font-semibold text-[#68677A]">
            {mod.trainers.join(", ")}
          </span>
        </span>
        <ChevronDown
          size={22}
          aria-hidden="true"
          className={`mt-1 shrink-0 text-[#FF008B] transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div id={panelId} className="acc-panel" data-open={open} role="region" aria-label={`Lekcje modułu ${mod.number}`}>
        <div>
          <ul className="space-y-2 border-t border-[#EAE6F0] px-5 py-5 sm:px-7">
            {mod.lessons.map((lesson) => (
              <li key={lesson} className="flex items-start gap-3 text-base leading-relaxed text-[#34343F]">
                <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FF008B]" aria-hidden="true" />
                {lesson}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default function Program() {
  const [openSet, setOpenSet] = useState(() => new Set([1]));
  const { active } = usePromotion();

  const toggle = (n) =>
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });

  return (
    <section id="program" className="bg-[#FAF8FC] py-16 sm:py-24">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <h2 className="text-2xl font-bold tracking-tight text-[#242344] sm:text-3xl lg:text-4xl">
              Program kursu
            </h2>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="program-expand-all-btn"
                onClick={() => setOpenSet(new Set(MODULES.map((m) => m.number)))}
                className="min-h-[44px] rounded-full border border-[#EAE6F0] bg-white px-4 py-2 text-sm font-bold text-[#242344] transition-colors duration-200 hover:border-[#FF008B] hover:text-[#FF008B]"
              >
                Rozwiń wszystkie
              </button>
              <button
                type="button"
                data-testid="program-collapse-all-btn"
                onClick={() => setOpenSet(new Set())}
                className="min-h-[44px] rounded-full border border-[#EAE6F0] bg-white px-4 py-2 text-sm font-bold text-[#242344] transition-colors duration-200 hover:border-[#FF008B] hover:text-[#FF008B]"
              >
                Zwiń wszystkie
              </button>
            </div>
          </div>
          <p className="mt-3 text-sm font-semibold text-[#68677A]">
            15 modułów · 69 lekcji
          </p>
        </Reveal>

        <div className="mt-8 space-y-3">
          {MODULES.map((mod) => (
            <ModuleItem
              key={mod.number}
              mod={mod}
              open={openSet.has(mod.number)}
              onToggle={() => toggle(mod.number)}
            />
          ))}
        </div>

        <Reveal>
          <div
            data-testid="program-cta-band"
            className="mt-10 overflow-hidden rounded-2xl bg-[#242344]"
          >
            <div className="airmail-edge" aria-hidden="true" />
            <div className="flex flex-col items-center gap-4 px-6 py-8 text-center sm:flex-row sm:justify-between sm:text-left">
            <div>
              {active ? (
                <>
                  <p className="text-white">
                    <del className="text-2xl font-extrabold sm:text-3xl">399 zł</del>{" "}
                    <span className="ml-2 rounded-xl bg-white/10 px-3 py-1 text-lg font-extrabold">
                      249 zł <span className="text-xs font-bold text-white/70">brutto</span>
                    </span>
                  </p>
                  <p className="mt-1.5 text-sm text-white/70">cena regularna / cena promocyjna</p>
                  <p className="mt-3 text-xs font-semibold text-white/80">
                    Tylko do 20 października 2026, do północy
                  </p>
                  <div className="mt-2.5 flex justify-center sm:justify-start">
                    <Countdown />
                  </div>
                </>
              ) : (
                <p className="text-3xl font-extrabold text-white">
                  399 zł <span className="text-base font-bold text-white/70">brutto</span>
                </p>
              )}
            </div>
            <BuyButton testid="program-cta-buy" location="program" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
